import { SourceCuration } from './SourceCuration';
import { ReactNode, useState } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { safeHotelImageUrl } from '@bandido/shared/types';
import { client } from '@frontend/features/common/services';
import { catalogAdminKey, useCatalogAdminCapability } from './queries';
import { HotelDetail, ConnectionPage, DetailConnection } from './detail';
import { EditorialSnapshot } from './editorial';

export function CatalogHotelDetail({
  hotelId,
  editor,
  preparation,
  destinations,
}: {
  hotelId: string;
  editor: ReactNode;
  preparation: ReactNode;
  destinations: ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const label = (key: string) => t(`hotelDetail.${key}`);
  const capability = useCatalogAdminCapability();
  const allowed = capability.data?.allowed === true && !capability.isError;
  const [tab, setTab] = useState('overview');
  const [visited, setVisited] = useState<string[]>([]);
  const detail = useQuery({
    queryKey: [...catalogAdminKey, 'detail', hotelId],
    queryFn: async () =>
      (await client.get<{ data: HotelDetail }>(`hotel-catalog-admin/hotels/${encodeURIComponent(hotelId)}`)).data.data,
    enabled: allowed && !!hotelId,
    retry: false,
  });
  if (capability.isLoading) return <CircularProgress aria-label={label('loading')} />;
  if (!allowed) return <Alert severity="warning">{label('unavailable')}</Alert>;
  if (detail.isLoading) return <CircularProgress aria-label={label('loading')} />;
  if (detail.isError || !detail.data) return (
    <Alert severity="error" action={<Button onClick={() => detail.refetch()}>{label('retry')}</Button>}>
      {label((detail.error as any)?.response?.status === 404 ? 'notFound' : 'error')}
    </Alert>
  );
  const hotel = detail.data,
    { publication } = hotel,
    content = publication?.content;
  const value = (v: unknown) => (v === null || v === undefined || v === '' ? label('missing') : String(v));
  const date = (v: string | null) => (v ? new Date(v).toLocaleString(i18n.language || 'es') : label('missing'));
  const field = (name: string, v: unknown) => (
    <Box sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
      <Typography variant="caption" color="text.secondary">
        {label(name)}
      </Typography>
      <Typography>{value(v)}</Typography>
    </Box>
  );
  const origin = (key: keyof EditorialSnapshot) => (
    <Chip size="small" variant="outlined" label={label(publication?.provenance[key] ?? 'missing')} />
  );
  const panel = (children: ReactNode) => (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
      {children}
    </Paper>
  );
  const location = content?.location;
  return (
    <Stack spacing={2} sx={{ minWidth: 0 }}>
      <Box>
        <Typography variant="overline" color="text.secondary">
          {label('title')}
        </Typography>
        <Typography variant="h4" component="h1" sx={{ overflowWrap: 'anywhere' }}>
          {hotel.display_name}
        </Typography>
        <Typography color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
          {label('ownId')}: {hotel.hotel_id}
        </Typography>
      </Box>
      <Stack direction="row" gap={1} flexWrap="wrap">
        <Chip color={hotel.status === 'active' ? 'success' : 'default'} label={label(hotel.status)} />
        <Chip variant="outlined" label={label(publication?.published ? 'published' : 'unpublished')} />
        {hotel.catalog_status !== 'active' && (
          <Chip variant="outlined" label={label(hotel.catalog_status ?? 'noEntry')} />
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {label('statusHelp')}
      </Typography>
      <Tabs
        value={tab}
        onChange={(_, next) => {
          setTab(next);
          setVisited((current) => (current.includes(next) ? current : [...current, next]));
        }}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        aria-label={label('sections')}
      >
        {['overview', 'connections', 'edit', 'destinations', 'preparation'].map((key) => (
          <Tab key={key} value={key} id={`hotel-tab-${key}`} aria-controls={`hotel-panel-${key}`} label={label(key)} />
        ))}
      </Tabs>
      <Box role="tabpanel" id={`hotel-panel-${tab}`} aria-labelledby={`hotel-tab-${tab}`}>
        {tab === 'overview' && (
          <Stack spacing={2}>
            {panel(
              <Stack spacing={2}>
                <Typography variant="h6">{label('identity')}</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
                  {field('ownId', hotel.hotel_id)}
                  {field('country', hotel.country_code)}
                  {field('locality', hotel.locality)}
                  {field('address', hotel.address)}
                  {field('stars', hotel.star_rating)}
                  {field(
                    'coordinates',
                    hotel.latitude === null || hotel.longitude === null
                      ? null
                      : `${hotel.latitude}, ${hotel.longitude}`,
                  )}
                </Box>
                {hotel.summary && field('identitySummary', hotel.summary)}
                <Divider />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {label('fastx')}
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {hotel.fastx_codes.length ? (
                      hotel.fastx_codes.map((code) => <Chip key={code} label={code} sx={{ maxWidth: '100%' }} />)
                    ) : (
                      <Typography>{label('noFastx')}</Typography>
                    )}
                  </Stack>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {label('identityUpdated')}: {date(hotel.updated_at)}
                </Typography>
              </Stack>,
            )}
            {hotel.preferred_source_content && !publication && (
              <Accordion defaultExpanded={!publication}>
                <AccordionSummary>
                  <Typography variant="h6">{t('hotelAdoption.preferredTitle')}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={2}>
                    <Alert severity="info">{t('hotelAdoption.preferredHelp')}</Alert>
                    <Typography>
                      {hotel.preferred_source_content.source_connection.account_name} ·{' '}
                      {hotel.preferred_source_content.source_connection.hotel_code}
                    </Typography>
                    <Typography variant="subtitle1">{hotel.preferred_source_content.content.display_name}</Typography>
                    <Typography>
                      {[
                        hotel.preferred_source_content.content.location?.address,
                        hotel.preferred_source_content.content.location?.city,
                        hotel.preferred_source_content.content.location?.country_code,
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    </Typography>
                    {hotel.preferred_source_content.content.descriptions?.map((d, i) => (
                      <Box key={i}>
                        <Typography variant="caption">{d.language.toUpperCase()}</Typography>
                        <Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{d.text}</Typography>
                      </Box>
                    ))}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4,1fr)' }, gap: 1 }}>
                      {hotel.preferred_source_content.content.images
                        ?.filter((img) => safeHotelImageUrl(img.url))
                        .map((img, i) => (
                          <Box
                            component="img"
                            key={img.url}
                            src={img.url}
                            alt={`${hotel.display_name} ${i + 1}`}
                            loading="lazy"
                            referrerPolicy="no-referrer"
                            sx={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', borderRadius: 1 }}
                          />
                        ))}
                    </Box>
                    <Button
                      onClick={() => {
                        setTab('preparation');
                        setVisited((current) =>
                          current.includes('preparation') ? current : [...current, 'preparation'],);
                      }}
                    >
                      {t('hotelAdoption.prepare')}
                    </Button>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            )}
            {panel(
              <Stack spacing={2}>
                <Typography variant="h6">{label('content')}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {label('contentHelp')}
                </Typography>
                {!publication ? (
                  <Alert severity="info">{label('noContent')}</Alert>
                ) : (
                  <>
                    {!publication.published && <Alert severity="warning">{label('withdrawn')}</Alert>}
                    <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
                      <Typography variant="subtitle1">
                        {label('contentName')}: {value(content?.display_name)}
                      </Typography>
                      {origin('display_name')}
                    </Stack>
                    <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
                      <Typography>
                        {label('category')}: {value(content?.category_code)}
                      </Typography>
                      {origin('category_code')}
                    </Stack>
                    <Box>
                      <Stack direction="row" gap={1} alignItems="center">
                        <Typography variant="subtitle2">{label('location')}</Typography>
                        {origin('location')}
                      </Stack>
                      <Typography sx={{ overflowWrap: 'anywhere' }}>
                        {location
                          ? [location.address, location.city, location.country_code].filter(Boolean).join(', ') ||
                            label('missing')
                          : label('missing')}
                      </Typography>
                      {typeof location?.latitude === 'number' && typeof location?.longitude === 'number' && (
                        <Typography variant="body2">
                          {location.latitude}, {location.longitude}
                        </Typography>
                      )}
                    </Box>
                    <Divider />
                    <Stack direction="row" gap={1} alignItems="center">
                      <Typography variant="subtitle2">{label('descriptions')}</Typography>
                      {origin('descriptions')}
                    </Stack>
                    {content?.descriptions?.length ? (
                      content.descriptions.map((d, index) => (
                        <Box key={index}>
                          <Typography variant="caption" color="text.secondary">
                            {d.language.toUpperCase()} · {d.kind}
                          </Typography>
                          <Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{d.text}</Typography>
                        </Box>
                      ))
                    ) : (
                      <Typography color="text.secondary">{label('missingDescriptions')}</Typography>
                    )}
                    <Stack direction="row" gap={1} alignItems="center">
                      <Typography variant="subtitle2">{label('images')}</Typography>
                      {origin('images')}
                    </Stack>
                    {content?.images?.length ? (
                      <Box
                        sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4,1fr)' }, gap: 1 }}
                      >
                        {content.images
                          .filter((image) => safeHotelImageUrl(image.url))
                          .map((image, index) => (
                            <Box
                              key={`${index}-${image.url}`}
                              component="img"
                              src={image.url}
                              alt={`${hotel.display_name} ${index + 1}`}
                              loading="lazy"
                              referrerPolicy="no-referrer"
                              sx={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', borderRadius: 1 }}
                            />
                          ))}
                      </Box>
                    ) : (
                      <Typography color="text.secondary">{label('missingImages')}</Typography>
                    )}
                    <Divider />
                    <Typography variant="subtitle2">{label('source')}</Typography>
                    {publication.source_connection ? (
                      <Typography sx={{ overflowWrap: 'anywhere' }}>
                        {publication.source_connection.account_name} ·{' '}
                        {publication.source_connection.connector === 'hotelbeds' ? 'Hotelbeds' : 'Travelgate'} ·{' '}
                        {publication.source_connection.hotel_code}
                        {publication.source_connection.access_code
                          ? ` · ${publication.source_connection.access_code}`
                          : ''}
                      </Typography>
                    ) : (
                      <Typography color="text.secondary">{label('globalSource')}</Typography>
                    )}
                    <Typography variant="body2">
                      {label('sourceObserved')}: {date(publication.source_observed_at)}
                    </Typography>
                    <Typography variant="body2">
                      {label('contentUpdated')}: {date(publication.updated_at)} · {label('version')}{' '}
                      {publication.version}
                    </Typography>
                  </>
                )}
              </Stack>,
            )}
          </Stack>
        )}
        {tab === 'connections' && <HotelConnections hotelId={hotelId} />}
        {visited.includes('edit') && <Box hidden={tab !== 'edit'}>{editor}</Box>}
        {visited.includes('destinations') && <Box hidden={tab !== 'destinations'}>{destinations}</Box>}
        {visited.includes('preparation') && <Box hidden={tab !== 'preparation'}>{preparation}</Box>}
      </Box>
    </Stack>
  );
}
export function HotelConnections({ hotelId }: { hotelId: string }) {
  const { t, i18n } = useTranslation();
  const label = (key: string) => t(`hotelDetail.${key}`);
  const [offset, setOffset] = useState(0);
  const [curating, setCurating] = useState<DetailConnection | null>(null);
  const page = useQuery({
    queryKey: [...catalogAdminKey, 'connections', hotelId, offset],
    queryFn: async () =>
      (
        await client.get<ConnectionPage>(`hotel-catalog-admin/hotels/${encodeURIComponent(hotelId)}/connections`, {
          params: { limit: 25, offset },
        })
      ).data,
    retry: false,
  });
  if (page.isLoading) return <CircularProgress aria-label={label('loading')} />;
  if (page.isError || !page.data) return (
    <Alert severity="error" action={<Button onClick={() => page.refetch()}>{label('retry')}</Button>}>
      {label('connectionError')}
    </Alert>
  );
  return (
    <Stack spacing={2}>
      <Typography variant="h6">{label('connections')}</Typography>
      <Alert severity="info">{t('hotelSourceCuration.addConnectionHelp')}</Alert>
      {curating && <SourceCuration hotelId={hotelId} source={curating} onClose={() => setCurating(null)} />}
      <Typography variant="body2" color="text.secondary">
        {label('connectionsHelp')}
      </Typography>
      {!page.data.data.length && <Alert severity="info">{label(offset ? 'emptyPage' : 'noConnections')}</Alert>}
      {page.data.data.map((c) => (
        <Paper variant="outlined" key={c.supplier_hotel_id} sx={{ p: 2, overflowWrap: 'anywhere' }}>
          <Stack spacing={1}>
            <Typography variant="subtitle1">
              {c.account_name} · {c.connector === 'hotelbeds' ? 'Hotelbeds' : 'Travelgate'}
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              <Chip size="small" label={label(c.link_status === 'active' ? 'linked' : 'disabledLink')} />
              <Chip
                variant="outlined"
                size="small"
                label={label(c.account_status === 'active' ? 'activeAccount' : 'disabledAccount')}
              />
            </Stack>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1 }}>
              {(
                [
                  ['connectionCode', c.hotel_code],
                  ['nativeCode', c.native_hotel_code],
                  ['access', c.access_code],
                  ['supplier', c.supplier_code],
                  ['provider', c.provider_code],
                  ['accountId', c.connector_account_id],
                ] as const
              ).map(([name, v]) => (
                <Box key={name}>
                  <Typography variant="caption" color="text.secondary">
                    {label(name)}
                  </Typography>
                  <Typography>{v || label('missing')}</Typography>
                </Box>
              ))}
            </Box>
            <Typography variant="caption" color="text.secondary">
              {label('refreshed')}:{' '}
              {c.last_refreshed_at
                ? new Date(c.last_refreshed_at).toLocaleString(i18n.language || 'es')
                : label('missing')}{' '}
              · {label(c.last_refresh_status ?? 'notRefreshed')}
            </Typography>
            <Button
              sx={{ alignSelf: 'flex-start' }}
              disabled={c.link_status !== 'active' || c.account_status !== 'active'}
              onClick={() => setCurating(c)}
            >
              {t('hotelSourceCuration.open')}
            </Button>
          </Stack>
        </Paper>
      ))}
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Button disabled={offset === 0 || page.isFetching} onClick={() => setOffset(Math.max(0, offset - 25))}>
          {label('previous')}
        </Button>
        <Typography variant="body2">
          {label('page')} {Math.floor(offset / 25) + 1}
        </Typography>
        <Button
          disabled={!page.data.meta.has_more || page.isFetching || offset >= 10000000}
          onClick={() => setOffset(offset + 25)}
        >
          {label('next')}
        </Button>
      </Stack>
    </Stack>
  );
}
